import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Users,
  Wrench,
  DollarSign,
  Clock,
  Target,
  ArrowRight,
  TrendingUp,
  BarChart3,
  Shield,
  CheckCircle,
  Search
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const AssetsLanding = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const assetCards = [
    {
      id: 'asset-management',
      title: 'Asset Management',
      description: 'Comprehensive asset tracking & management system',
      icon: <Package size={24} />,
      path: '/assets/management',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Asset tracking', 'Inventory management', 'Asset details']
    },
    {
      id: 'assignment-usage',
      title: 'Assignment & Usage',
      description: 'Track asset assignments & usage patterns',
      icon: <Users size={24} />,
      path: '/assets/assignment',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Check-in/check-out', 'Allocation tracking', 'Usage analytics']
    },
    {
      id: 'maintenance-ops',
      title: 'Maintenance & Ops',
      description: 'Preventive & corrective maintenance management',
      icon: <Wrench size={24} />,
      path: '/assets/maintenance',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Preventive maintenance', 'Corrective maintenance', 'Work orders']
    },
    {
      id: 'financial-valuation',
      title: 'Financial & Valuation',
      description: 'Asset depreciation, valuation & insurance tracking',
      icon: <DollarSign size={24} />,
      path: '/assets/financial',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Depreciation tracking', 'Valuation reports', 'Insurance management']
    },
    {
      id: 'asset-lifecycle',
      title: 'Asset Lifecycle',
      description: 'Complete asset lifecycle from acquisition to disposal',
      icon: <Clock size={24} />,
      path: '/assets/lifecycle',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Acquisition tracking', 'Lifecycle stages', 'Disposal management']
    },
    {
      id: 'asset-categorization',
      title: 'Asset Categorization',
      description: 'Organize assets by type, category & classification',
      icon: <Target size={24} />,
      path: '/assets/categorization',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['IT equipment', 'Real estate', 'Licenses', 'Categories']
    }
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  const filteredCards = assetCards.filter((card) => {
    const query = searchQuery.toLowerCase();
    return card.title.toLowerCase().includes(query);
  });

  return (
    <>
      <style>{`
        .asset-search-input {
          width: 300px;
        }
        @media (max-width: 768px) {
          .asset-search-input {
            width: 100%;
          }
        }
      `}</style>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb 
        rightContent={
          <div style={{ position: 'relative' }} className="asset-search-input">
            <Search 
              size={18} 
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6B7280'
              }} 
            />
            <input
              type="text"
              placeholder="Search asset modules..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
                backgroundColor: '#FFFFFF',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--gold-primary)'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>
        }
      />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Assets Management"
        description="Comprehensive asset lifecycle management and tracking system"
      />
      <div className="page-container">
        {/* Asset Management Cards */}
        <div
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          {filteredCards.length > 0 ? (
            filteredCards.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => handleCardClick(card.path)}
            >
              {/* Top Accent Line */}
              <div
                style={{
                  height: '4px',
                  background: `var(--gold-primary)`,
                  width: '100%'
                }}
              />

              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: card.iconBg || '#FFF9E6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px'
                    }}
                  >
                    <div style={{ color: card.iconLine }} className="asset-card-icon">
                      {card.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      margin: '0',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {card.title}
                    </h4>
                  </div>
                  <ArrowRight
                    size={20}
                    className="asset-card-arrow"
                  />
                </div>

                {/* Description */}
                <p style={{
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  margin: '0 0 12px 0',
                  lineHeight: '1.4',
                  fontFamily: 'var(--font-family)'
                }}>
                  {card.description}
                </p>

                {/* Features */}
                <div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {card.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '6px',
                          fontWeight: '500',
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            ))
          ) : (
            <div style={{ 
              gridColumn: '1 / -1', 
              padding: '60px 20px', 
              textAlign: 'center',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px dashed #D1D5DB'
            }}>
              <Search size={32} style={{ color: '#9CA3AF', marginBottom: '16px' }} />
              <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#374151', margin: '0 0 8px 0' }}>
                No results found
              </h3>
              <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                We couldn't find any asset modules matching "{searchQuery}"
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AssetsLanding;
